import React, { useEffect, useMemo } from 'react';
import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import Head from 'next/head';
import Router from 'next/router';
import { gql } from '@soundxyz/gql-string';
import type { SeoProps } from '@components/Seo';
import { Seo } from '@components/Seo';
import { SongView } from '@components/Song';
import { Spinner } from '@components/basics/Spinner';
import type { ExecutionResultWithData, ReleasePageQuery } from '@graphql/reactQuery';
import {
  fetchGQL,
  getFragment,
  ReleasePageContentFragmentDoc,
  ReleasePageDocument,
  useQuery,
  ViralSoundsLandingDocument,
  ViralSoundsRankedListItemFragmentDoc,
} from '@graphql/reactQuery';
import { queryAudioFromTrack } from '@store/audio/AudioFetcher';
import { getReleaseOpenGraphImage } from '@utils/helpers';
import type { NonNullableObject } from '@utils/types';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ReleasePageContent on Release {
    id
    title
    titleSlug
    type
    externalUrl
    behindTheMusic
    webappUri
    numSold
    contractAddress
    isFirstPhaseCompleted
    artist {
      id
      name
      soundHandle
      user {
        id
        twitterHandle
        publicAddress
      }
    }
    track {
      id
    }
    coverImage {
      id
      url
    }
    staticCoverImage {
      id
      url
    }
    ...SongView
    ...CollectModal
  }
  query ReleasePage($soundHandle: String!, $releaseSlug: String!) {
    mintedRelease(soundHandle: $soundHandle, releaseSlug: $releaseSlug) {
      id
      ...ReleasePageContent
      ...WaveformCommentsReleaseInfo
    }
  }
`);

const SOLD_OUT_ISR_REVALIDATE_PERIOD = 120;
const PENDING_ISR_REVALIDATE_PERIOD = 20;

type PathParams = {
  soundHandle: string;
  releaseSlug: string;
};

export const getStaticProps: GetStaticProps<PageProps, PathParams> = async ({ params }) => {
  const { soundHandle, releaseSlug } = params || {};

  if (!soundHandle || Array.isArray(soundHandle) || !releaseSlug || Array.isArray(releaseSlug)) {
    return {
      redirect: {
        destination: '/404',
        permanent: true,
      },
    };
  }

  return Promise.all([
    fetchGQL(ReleasePageDocument, {
      variables: { soundHandle, releaseSlug },
    }),
  ]).then(async ([releaseDataQuery]) => {
    if (!releaseDataQuery.data.mintedRelease) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
        // we don't really want to cache this, but also don't want to throw an error
        // unclear if revalidate 0 will cause issues, so setting this to 1
        revalidate: 1,
      };
    }

    const releaseData = {
      ...releaseDataQuery,
      data: { ...releaseDataQuery.data, mintedRelease: releaseDataQuery.data.mintedRelease },
    };

    const release = getFragment(ReleasePageContentFragmentDoc, releaseData.data.mintedRelease);

    // for non-single release type, redirect to externalUrl and fallback to homepage
    if (release.type === 'ALBUM') {
      return {
        redirect: {
          destination: release.externalUrl ?? '/',
          permanent: false,
        },
        revalidate: PENDING_ISR_REVALIDATE_PERIOD,
      };
    }

    const [trackInfo] = await Promise.all([
      // It's refetched on authenticated users anyways
      queryAudioFromTrack({
        trackId: release.track.id,
      }),
    ]);

    const props: PageProps = {
      seo: {
        title: `${release.artist.name} - ${release?.title}`,
        twitterHandle: release.artist.user.twitterHandle || null,
        description: release.behindTheMusic.slice(0, 160) + '...',
        /**
         * If cover image is gif, we generate a static cover image
         * If not, we can use the cover image directly
         */
        image:
          getReleaseOpenGraphImage({ releaseId: release.id }) ||
          release.staticCoverImage?.url ||
          release.coverImage.url,
        url: null,
        audioUrl: trackInfo?.url || null,
      },
      releaseData,
      soundHandle: release.artist.soundHandle || soundHandle,
      releaseSlug: release.titleSlug,
    };

    return {
      props,
      revalidate: release.isFirstPhaseCompleted
        ? SOLD_OUT_ISR_REVALIDATE_PERIOD
        : PENDING_ISR_REVALIDATE_PERIOD,
    };
  });
};

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  return Promise.all([
    fetchGQL(ViralSoundsLandingDocument, {
      variables: {
        pagination: { first: 10, after: null },
      },
    }),
  ]).then(([viralSounds]) => {
    return {
      paths: viralSounds.data.currentReleaseChart.chartRanks.edges.reduce(
        (acc: GetStaticPathsResult<PathParams>['paths'], { node }) => {
          const chartRank = getFragment(ViralSoundsRankedListItemFragmentDoc, node);
          if (!!chartRank.release.artist.soundHandle && !!chartRank.release.titleSlug) {
            acc.push({
              params: {
                soundHandle: chartRank.release.artist.soundHandle,
                releaseSlug: chartRank.release.titleSlug,
              },
            });
          }

          return acc;
        },
        [],
      ),
      fallback: true,
    };
  });
};

interface PageProps {
  seo: SeoProps;
  soundHandle: string;
  releaseSlug: string;
  releaseData: ExecutionResultWithData<NonNullableObject<ReleasePageQuery>>;
}

/**
 * NOTE: This page component is setup to only work in SSR mode.
 * If you need it to work in non-SSR environment you need to account for loading states.
 */
export default function PublicReleasePage(props: PageProps | Partial<PageProps>) {
  const { releaseData, seo, soundHandle: soundHandleProp, releaseSlug: releaseSlugProp } = props;

  // When using fallback, we don't have access to params through next router
  const soundHandle = useMemo(() => {
    if (soundHandleProp) return soundHandleProp;

    if (typeof window !== 'undefined') return new URL(window.location.href).pathname.split('/')[1];

    return null;
  }, [soundHandleProp]);

  const releaseSlug = useMemo(() => {
    if (releaseSlugProp) return releaseSlugProp;

    if (typeof window !== 'undefined') return new URL(window.location.href).pathname.split('/')[2];

    return null;
  }, [releaseSlugProp]);

  const { data, isInitialLoading, isError, isRefetchError, isLoadingError } = useQuery(
    ReleasePageDocument,
    {
      variables: !!soundHandle && !!releaseSlug && { soundHandle, releaseSlug },
      initialData: releaseData,
      staleTime: 0,
    },
  );

  const release = getFragment(
    ReleasePageContentFragmentDoc,
    data?.data.mintedRelease || releaseData?.data.mintedRelease,
  );

  const hasRelease = !!release;

  useEffect(() => {
    if (hasRelease || isInitialLoading || isError || isRefetchError || isLoadingError) return;

    Router.replace('/404');
  }, [isInitialLoading, hasRelease, isError, isRefetchError, isLoadingError]);

  // Only used on fallback, when cache can't be found
  if (!release) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Head>
        {/* https://warpcast.notion.site/NFT-open-graph-5a64ca22d2374f99832bc4b91c386c46 */}
        <meta property="eth:nft:collection" content={release.title} />
        <meta property="eth:nft:contract_address" content={release.contractAddress} />
        <meta property="eth:nft:creator_address" content={release.artist.user.publicAddress} />
        <meta property="eth:nft:schema" content="erc721" />
        <meta property="eth:nft:mint_count" content={release.numSold.toString()} />
        <meta property="eth:nft:mint_url" content={release.webappUri} />
      </Head>
      <Seo {...seo} />
      <SongView release={release} />
    </>
  );
}
